import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { apiList, sourcing } from "@/content/site";
import { sourcingForm } from "@/content/forms";
import usePageMeta from "@/hooks/usePageMeta";
import { PageHero } from "@/components/sections/PageHero";
import { CategoryGrid } from "@/components/sections/CategoryGrid";
import { StepFlow } from "@/components/sections/StepFlow";
import { CtaBand } from "@/components/sections/CtaBand";
import { RegulatoryNote } from "@/components/sections/RegulatoryNote";
import { FormSection } from "@/components/forms/FormSection";
import { SectionHeader } from "@/components/common/SectionHeader";
import { Rich } from "@/components/common/Rich";
import { Reveal } from "@/components/motion/Reveal";

const scrollToForm = () => document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth" });

export default function Sourcing() {
  usePageMeta(sourcing.meta);
  const product = useSearchParams()[0].get("product") || "";
  const initialValues = product ? { enquiry_type: "API", product_name: product } : undefined;
  return (
    <>
      <PageHero hero={sourcing.hero} testId="sourcing-hero">
        <RegulatoryNote className="mt-6 max-w-2xl" testId="sourcing-regulatory-note" />
        <button
          type="button"
          onClick={scrollToForm}
          className="mt-10 rounded-none bg-accent px-7 py-3.5 text-[15px] font-semibold text-accent-ink transition-transform duration-300 hover:-translate-y-0.5"
          data-testid="sourcing-hero-enquire-button"
        >
          Send a sourcing enquiry
        </button>
      </PageHero>

      <section className="section-y border-t border-line" data-testid="sourcing-pillars">
        <div className="container-x">
          <SectionHeader eyebrow="How SPC sources" title="Qualified suppliers. Complete paperwork. Delivered." className="mb-14" />
          <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
            {sourcing.pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08} className="border-t border-ink/80 pt-6" data-testid={`sourcing-pillar-${i}`}>
                <h3 className="font-display text-2xl font-semibold tracking-[-0.03em] text-ink">{p.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-muted">
                  <Rich text={p.body} />
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-y border-t border-line" data-testid="sourcing-categories">
        <div className="container-x">
          <SectionHeader eyebrow="Categories" title="What we source." className="mb-14" />
          <CategoryGrid categories={sourcing.categories} />
          <Reveal className="mt-10 flex flex-col gap-6 border border-ink bg-ink p-8 text-paper sm:p-10 lg:flex-row lg:items-center lg:justify-between" data-testid="sourcing-api-list-teaser">
            <div className="max-w-2xl">
              <p className="font-mono text-[12px] uppercase tracking-[0.14em] text-paper/70">{apiList.teaser.eyebrow}</p>
              <p className="mt-3 font-display text-2xl font-semibold tracking-[-0.03em] sm:text-3xl">{apiList.teaser.title}</p>
              <p className="mt-3 text-[15px] leading-relaxed text-paper/80">{apiList.teaser.body}</p>
            </div>
            <Link to="/active-pharmaceutical-ingredients" className="inline-flex shrink-0 items-center gap-2 self-start bg-paper px-6 py-3.5 text-[15px] font-semibold text-ink transition-colors duration-300 hover:bg-accent hover:text-accent-ink lg:self-auto" data-testid="sourcing-api-list-link">
              {apiList.teaser.cta} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="section-y border-t border-line" data-testid="sourcing-how-it-works">
        <div className="container-x">
          <SectionHeader eyebrow="How it works" title="Four steps from enquiry to supply." className="mb-16" />
          <StepFlow steps={sourcing.steps} />
        </div>
      </section>

      <FormSection key={product} id="enquiry" intro={sourcing.formIntro} config={sourcingForm} idPrefix="sourcing" initialValues={initialValues} />
      <CtaBand />
    </>
  );
}
